import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  isBranch3ImportSaleVoucherRow,
  isImportSaleVoucherRow,
  isMainBranchImportSaleVoucherRow,
  isSparewayImportSaleVoucherRow,
} from "../../../services/salesImportTransactionDataService";
import {
  buildHolidayYmdSetFromDocs,
  getWorkingDayYmdsFullMonth,
  holidayDocDateToYmd,
  isEmployeeCashLeaveDoc,
  isSunday,
} from "../../../helpers/payrollAttendanceCore";
import { loadMasterGroupNames } from "./brandGroupBranchReportService";
import {
  lineAmountOnly,
  loadLineChunkRowsForDateRange,
  normalizeVoucherType,
} from "./salesLineChunksReader";
import {
  normalizeSalesImportBranchId,
  salesImportBranchDisplayName,
  salesImportBranchIdFromRow,
  salesImportBranchOutwardSupplyLabel,
} from "../config/salesImportBranchIds";
import { rowTranDateYmd } from "../utils/dateParse";

export const ITEMS_ON_SALE_ALL_GROUPS = "__all__";
export const ITEMS_ON_SALE_DEFAULT_BRANCH_ID = "10";

/** Prefer voucher-type match for the branch; BranchID is only a fallback (never rejects a voucher match). */
export function isSaleVoucherRowForBranch(row, branchId) {
  const id = normalizeSalesImportBranchId(branchId);
  if (!id) return false;

  if (id === "10" && isBranch3ImportSaleVoucherRow(row)) return true;
  if (id === "11" && isSparewayImportSaleVoucherRow(row)) return true;
  if (id === "1" && isMainBranchImportSaleVoucherRow(row)) return true;

  // Fallback when voucher labels are odd but BranchID + any sale voucher match.
  return isImportSaleVoucherRow(row) && salesImportBranchIdFromRow(row) === id;
}

function calendarYmd(year, monthIndex, day) {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function calendarMonthRange(year, monthIndex) {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    fromDate: calendarYmd(year, monthIndex, 1),
    toDate: calendarYmd(year, monthIndex, lastDay),
  };
}

function monthLabel(year, monthIndex) {
  return new Date(year, monthIndex, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function weekdayLabel(ymdStr) {
  const [y, m, d] = ymdStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleString("en-IN", { weekday: "short" });
}

function rawGroupFromRow(row) {
  return String(row.itemGroup ?? row.itemgroup ?? row["Item Group"] ?? "").trim();
}

function buildGroupLookup(masterGroups) {
  const lookup = new Map();
  for (const name of masterGroups) {
    lookup.set(name.toLowerCase(), name);
  }
  return lookup;
}

function matchMasterGroup(raw, groupLookup, masterGroups) {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (groupLookup.has(lower)) return groupLookup.get(lower);
  for (const master of masterGroups) {
    if (master.toLowerCase() === lower) return master;
  }
  const textUpper = raw.toUpperCase();
  for (const master of [...masterGroups].sort((a, b) => b.length - a.length)) {
    const masterUpper = master.toUpperCase();
    if (textUpper === masterUpper || textUpper.includes(masterUpper)) return master;
  }
  return raw;
}

function resolveRowGroup(row, masterGroups, groupLookup) {
  return matchMasterGroup(rawGroupFromRow(row), groupLookup, masterGroups) || "Unknown";
}

function listSundaysInMonth(year, monthIndex) {
  const end = new Date(year, monthIndex + 1, 0);
  const out = [];
  for (let d = new Date(year, monthIndex, 1); d <= end; d.setDate(d.getDate() + 1)) {
    if (isSunday(d)) out.push(calendarYmd(year, monthIndex, d.getDate()));
  }
  return out;
}

async function loadCompanyHolidaysInMonth(year, monthIndex) {
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  try {
    const snap = await getDocs(collection(db, "holidays"));
    const holidayYmdSet = buildHolidayYmdSetFromDocs(snap.docs, monthStart, monthEnd);
    const holidayDetails = [];
    for (const docSnap of snap.docs) {
      const data = docSnap.data() || {};
      if (isEmployeeCashLeaveDoc(data)) continue;
      const ymdStr = holidayDocDateToYmd(data);
      if (!ymdStr || !holidayYmdSet.has(ymdStr)) continue;
      holidayDetails.push({
        ymd: ymdStr,
        name: String(data.name || data.description || "Holiday").trim() || "Holiday",
      });
    }
    holidayDetails.sort((a, b) => a.ymd.localeCompare(b.ymd));
    return { holidayYmdSet, holidayDetails };
  } catch {
    return { holidayYmdSet: new Set(), holidayDetails: [] };
  }
}

async function safeLoadMasterGroups() {
  try {
    return await loadMasterGroupNames();
  } catch {
    return [];
  }
}

function itemKeyFromRow(row) {
  const partNo = String(row.partNo ?? row.PartNo ?? row.productId ?? "").trim();
  const itemName = String(row.itemName ?? row.ItemName ?? "").trim();
  return partNo || itemName || "unknown";
}

function bumpDayItem(bucket, row, groupName) {
  const key = itemKeyFromRow(row);
  const partNo = String(row.partNo ?? row.PartNo ?? "").trim();
  const itemName = String(row.itemName ?? row.ItemName ?? "").trim();
  const voucherNo = String(row.voucherNo ?? row.VoucherNo ?? "").trim();
  const qty = Math.abs(Number(row.qty ?? row.Qty) || 0);
  const amount = Math.abs(lineAmountOnly(row));

  const prev = bucket.get(key) || {
    key,
    partNo,
    itemName: itemName || partNo || "—",
    groupName,
    qty: 0,
    amount: 0,
    lineCount: 0,
    invoiceNos: new Set(),
  };
  prev.qty += qty;
  prev.amount += amount;
  prev.lineCount += 1;
  if (voucherNo) prev.invoiceNos.add(voucherNo);
  bucket.set(key, prev);
}

function finalizeDayItems(bucket) {
  return [...bucket.values()]
    .map(({ invoiceNos, ...rest }) => ({
      ...rest,
      invoiceCount: invoiceNos.size,
    }))
    .sort((a, b) => {
      if (b.amount !== a.amount) return b.amount - a.amount;
      return (a.itemName || "").localeCompare(b.itemName || "");
    });
}

/**
 * Daily item sales on working days (Mon–Sat, excluding company holidays).
 */
export async function fetchItemsOnSaleDailyReport(params) {
  const year = Number(params.year);
  const monthIndex = Number(params.monthIndex);
  const branchId = normalizeSalesImportBranchId(params.branchId);
  const groupFilter =
    params.groupName && params.groupName !== ITEMS_ON_SALE_ALL_GROUPS
      ? String(params.groupName).trim()
      : "";

  if (!branchId) {
    throw new Error("Branch is required.");
  }
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    throw new Error("Invalid year/month.");
  }

  const selectedRange = calendarMonthRange(year, monthIndex);
  const [masterGroups, holidayPack] = await Promise.all([
    safeLoadMasterGroups(),
    loadCompanyHolidaysInMonth(year, monthIndex),
  ]);
  const groupLookup = buildGroupLookup(masterGroups);
  const workingDays = getWorkingDayYmdsFullMonth(
    new Date(year, monthIndex, 1),
    holidayPack.holidayYmdSet
  );
  const workingDaySet = new Set(workingDays);
  const sundays = listSundaysInMonth(year, monthIndex);

  // Load all lines in the month (no voucher filter in Firestore pass — filter in memory).
  const { rows: allRows, chunksLoaded } = await loadLineChunkRowsForDateRange(
    selectedRange,
    (rowCount, chunks) => params.onProgress?.(rowCount, chunks)
  );

  const voucherTypesSeen = new Map();
  let branchSaleCandidates = 0;
  let sundaySaleLines = 0;
  let holidaySaleLines = 0;
  let noDateLines = 0;

  const rows = [];
  for (const row of allRows) {
    const vt = normalizeVoucherType(row);
    voucherTypesSeen.set(vt, (voucherTypesSeen.get(vt) || 0) + 1);
    if (!isSaleVoucherRowForBranch(row, branchId)) continue;
    branchSaleCandidates += 1;
    rows.push(row);
  }

  const itemsByDay = new Map();
  for (const ymdStr of workingDays) {
    itemsByDay.set(ymdStr, new Map());
  }

  let matchedLines = 0;
  for (const row of rows) {
    const ymdStr = rowTranDateYmd(row);
    if (!ymdStr) {
      noDateLines += 1;
      continue;
    }

    if (sundays.includes(ymdStr)) {
      sundaySaleLines += 1;
      continue;
    }
    if (holidayPack.holidayYmdSet.has(ymdStr)) {
      holidaySaleLines += 1;
      continue;
    }
    if (!workingDaySet.has(ymdStr)) {
      // Outside selected month working-day set (should be rare after date-range load).
      continue;
    }

    const groupName = resolveRowGroup(row, masterGroups, groupLookup);
    if (groupFilter && groupName !== groupFilter) continue;

    const bucket = itemsByDay.get(ymdStr);
    if (!bucket) continue;
    bumpDayItem(bucket, row, groupName);
    matchedLines += 1;
  }

  const days = workingDays.map((ymdStr) => {
    const rawBuckets = itemsByDay.get(ymdStr) || new Map();
    const items = finalizeDayItems(rawBuckets);
    const dayInvoiceNos = new Set();
    for (const entry of rawBuckets.values()) {
      for (const inv of entry.invoiceNos) dayInvoiceNos.add(inv);
    }
    const totals = items.reduce(
      (acc, item) => {
        acc.qty += item.qty;
        acc.amount += item.amount;
        acc.lineCount += item.lineCount;
        acc.itemCount += 1;
        return acc;
      },
      { qty: 0, amount: 0, lineCount: 0, itemCount: 0, invoiceCount: dayInvoiceNos.size }
    );
    return { ymd: ymdStr, weekday: weekdayLabel(ymdStr), items, totals };
  });

  const grandTotals = days.reduce(
    (acc, day) => {
      acc.qty += day.totals.qty;
      acc.amount += day.totals.amount;
      acc.lineCount += day.totals.lineCount;
      acc.itemCount += day.totals.itemCount;
      acc.workingDaysWithSales += day.items.length > 0 ? 1 : 0;
      return acc;
    },
    { qty: 0, amount: 0, lineCount: 0, itemCount: 0, workingDaysWithSales: 0 }
  );

  const voucherTypeSamples = [...voucherTypesSeen.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([voucherType, count]) => ({ voucherType, count }));

  return {
    meta: {
      year,
      monthIndex,
      monthLabel: monthLabel(year, monthIndex),
      branchId,
      branchLabel: salesImportBranchOutwardSupplyLabel(branchId),
      branchDisplayName: salesImportBranchDisplayName(branchId),
      groupName: groupFilter || ITEMS_ON_SALE_ALL_GROUPS,
      groupLabel: groupFilter || "All groups",
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      workingDayCount: workingDays.length,
      sundayCount: sundays.length,
      holidayCount: holidayPack.holidayDetails.length,
      scannedLines: allRows.length,
      branchSaleCandidates,
      matchedLines,
      sundaySaleLines,
      holidaySaleLines,
      noDateLines,
      chunksLoaded,
      grandTotals,
      voucherTypeSamples,
    },
    days,
    skipped: {
      sundays,
      holidays: holidayPack.holidayDetails,
    },
    groupOptions: masterGroups,
  };
}
